import { Dispatch } from 'redux';
import dayjs from 'dayjs';
import { RootState, Thunk } from 'store/types';
import {
  triageGyantCurrentCaseIdSelector,
  triageGyantSessionTokenDataSelector
} from 'store/triageGyant/selectors';
import { profileConsumerDisplayNameSelector, profilePhonesSelector } from 'store/profile/selectors';
import { eVisitSLASelector } from 'store/eVisit/selectors';
import { isDfdAuthenticatedSelector } from 'store/authentication/selectors';
import { navigateToEVisit } from './actions';
import { EVisitFormData, SLA } from './types';

export const RESET_E_VISIT_SESSION_TOKEN = 'E_VISIT.RESET_E_VISIT_SESSION_TOKEN';
export const SET_E_VISIT_SESSION_TOKEN = 'E_VISIT.SET_E_VISIT_SESSION_TOKEN';
export const setEVisitSessionToken = (token: string) => ({
  type: SET_E_VISIT_SESSION_TOKEN,
  payload: token
});

export const SEND_E_VISIT_DATA = 'E_VISIT.SEND_E_VISIT_DATA';
export const SEND_E_VISIT_DATA_SUCCESS = 'E_VISIT.SEND_E_VISIT_DATA_SUCCESS';
export const SEND_E_VISIT_DATA_FAIL = 'E_VISIT.SEND_E_VISIT_DATA_FAIL';
export const sendEVisitFormData = (caseId: string, data: EVisitFormData) => ({
  type: SEND_E_VISIT_DATA,
  payload: {
    client: 'symptomChecker',
    request: {
      url: `/case-data`,
      method: 'post',
      headers: { 'x-case-id': caseId },
      data
    }
  }
});

export const START_E_VISIT = 'E_VISIT.START_E_VISIT';
export const START_E_VISIT_SUCCESS = 'E_VISIT.START_E_VISIT_SUCCESS';
export const START_E_VISIT_FAIL = 'E_VISIT.START_E_VISIT_FAIL';
export const startEVisitSession = (selectedLocationState: string): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  try {
    dispatch({ type: START_E_VISIT });

    const state = getState();
    const caseId = triageGyantCurrentCaseIdSelector(state);
    const sessionToken = triageGyantSessionTokenDataSelector(state);
    const eVisitSLA = eVisitSLASelector(state).find((i: SLA) => i.state === selectedLocationState);

    // Compose Form Data
    const signedBy = profileConsumerDisplayNameSelector(state);
    const signedDate = dayjs().format('MM/DD/YYYY HH:mm:ss');
    const relationshipToPatient = 'PATIENT';
    const phoneNumber = profilePhonesSelector(state)?.[0]?.value;
    const data = {
      forms: [
        {
          docName: 'NoticeofPrivacy',
          docAliasId: '50086a',
          witness: 'Sign By My Health+',
          signedBy,
          signedDate,
          relationshipToPatient,
          phoneNumber
        },
        {
          docName: 'ConsentandConditionsofService',
          docAliasId: '50064',
          witness: 'Sign By My Health+',
          signedBy,
          signedDate,
          relationshipToPatient,
          phoneNumber
        }
      ],
      sla: eVisitSLA
    };

    const result = await dispatch(sendEVisitFormData(caseId, data));

    if (result.error) {
      return dispatch({ type: START_E_VISIT_FAIL });
    }

    await dispatch(setEVisitSessionToken(sessionToken));
    navigateToEVisit(dispatch);
    return dispatch({ type: START_E_VISIT_SUCCESS, payload: selectedLocationState });
  } catch (err) {
    return dispatch({ type: START_E_VISIT_FAIL });
  }
};

// Check EVisit eligibility
export const CHECK_E_VISIT_ELIGIBILITY = 'E_VISIT.CHECK_E_VISIT_ELIGIBILITY';
export const CHECK_E_VISIT_ELIGIBILITY_SUCCESS = 'E_VISIT.CHECK_E_VISIT_ELIGIBILITY_SUCCESS';
export const CHECK_E_VISIT_ELIGIBILITY_FAIL = 'E_VISIT.CHECK_E_VISIT_ELIGIBILITY_FAIL';
export const checkEVisitEligibility = (caseId: string): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const isAuthenticated = isDfdAuthenticatedSelector(getState());
  if (!isAuthenticated) {
    return dispatch({
      type: CHECK_E_VISIT_ELIGIBILITY_FAIL,
      error: new Error('Guest user ineligible for E-Visit')
    });
  }
  return dispatch({
    type: CHECK_E_VISIT_ELIGIBILITY,
    payload: {
      client: 'symptomChecker',
      request: {
        url: `/eligibility/exclusion-criteria`,
        method: 'get',
        headers: { 'x-case-id': caseId }
      }
    }
  });
};
